import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import {
    connectAggregationToObservable } from '@pendo/agg-connect';
import { Subject,
    of } from 'rxjs';
import { mergeMap,
    switchMap,
    map } from 'rxjs/operators';
import { http } from '@pendo/http';
import { formatValueForSchema } from '@/utils/formatters.js';
import { getVisitorDisplayName } from '@/utils/visitors';
import { formatDateRangeForQuery } from '@/dashboards-v2/components/dashboard-filter-bar/util/date-range';
import { getFrontendVersion } from '@/utils/frontend-version';
import { buildReplayRecordingRouteObject } from '@/session-replay/helpers/common.util';
import { hasAnyMetadataFilter } from '@/ai-agents/aggregations/ai-agent-conversation-events-helpers';
import { queryMetadataValues,
} from '@/stores/api/metadata.api';
import { LOADING_STATES } from '@/constants';
import { getDefaultSegments } from '@/utils/segments';
import { getActiveSubscription } from '@/data/session/session-globals';
import {
    AI_AGENT_TYPES,
    SAMPLING_THRESHOLD,
    WIDGET_TYPE_NAME_MAP,
    TRACKED_USE_CASE_STATUS,
    isUserMessageType,
    isSystemMessageType,
} from './ai-agents.constants.js';
import { retentionWindowField } from './ai-agent-settings/ai-agent-retention.utils';
import { i18n_t as $t } from '@/utils/i18n';

// Builds metadata columns from kind options and user settings visibility.
// Ensures consistent formatter behavior and primary key/category mapping across tables.
export function buildMetadataColumns ({
    kindOptions = [],
    userSettingsVisibleColumns = [],
    hasAccountLevelAnalyticsEnabled = false,
}) {
    const visibleProps = userSettingsVisibleColumns.map((column) => (typeof column === 'string' ? column : column.prop));

    // Filter to visitor/account kinds, optionally hide account-level when disabled
    const visitorAndAccountOptions = (Array.isArray(kindOptions) ? kindOptions : []).filter(
        (option) => option && (option.kind === 'visitor' || option.kind === 'account'),
    );

    const filteredOptions = hasAccountLevelAnalyticsEnabled
        ? visitorAndAccountOptions
        : visitorAndAccountOptions.filter(
            (option) =>
                option.kind !== 'account' &&
                option.id !== 'visitor_auto_accountids' &&
                option.id !== 'visitor_agent_parentaccount',
        );

    const metadataColumns = filteredOptions.map((option) => {
        const prop = option.id;
        const { schema } = option;

        let formatter;
        switch (schema) {
            case 'date':
                formatter = (row) => {
                    if (!row[prop]) return '';

                    return formatValueForSchema(row[prop], schema, 'short');
                };
                break;
            default:
                formatter = (row) => formatValueForSchema(row[prop], schema);
                break;
        }

        const column = {
            label: option.optionLabel,
            prop,
            sortable: true,
            visible: visibleProps.includes(prop),
            functionName: option.functionName,
            fieldName: option.field,
            kind: option.kind,
            schema: option.schema,
            columnName: prop,
            formatter,
        };

        if (option.schema === 'trending') {
            column.slot = prop;
        }

        if (option.kind === 'visitor') {
            column.category = 'Visitor Data';
            column.primaryKeys = ['visitorId'];
        } else if (option.kind === 'account') {
            column.category = 'Account Data';
            column.primaryKeys = ['accountId'];
        }

        return column;
    });

    return sortBy(metadataColumns, ['category', (column) => column.label.toLowerCase()]);
}

export const aiAgentsApi = '/api/s/_SID_/agentanalytics';

export const agenticSchemaApi = '/api/s/_SID_/metadata/event/agentic/schema';

export async function fetchAgenticSchemaUniqueValues (fieldName) {
    const { data } = await http.get(`${agenticSchemaApi}/${fieldName}`);

    return (data?.uniqueValues || [])
        .map((value) => value.replace(/^"|"$/g, ''))
        .filter((value) => value.trim() !== '');
}

export async function fetchEventPropertyValues ({ kind = 'event', group = 'custom', field } = {}) {
    if (!field) return [];
    const { data } = await queryMetadataValues(kind, group, field);
    const values = Array.isArray(data?.values) ? data.values : [];

    return values.filter((value) => value !== null && value !== undefined && value !== '');
}

export const slackDigestApi = '/api/s/_SID_/slackdigest';

export const isEventTypeUserOrSystemMessage = (eventType) => {
    return isUserMessageType(eventType) || isSystemMessageType(eventType);
};

export function getFormattedAiAgentWidgetName (reportName, widgetType) {
    const widgetTypeName = WIDGET_TYPE_NAME_MAP[widgetType];

    if (reportName) {
        if (widgetTypeName && reportName) {
            return `${widgetTypeName} for ${reportName}`;
        }

        return reportName;
    }

    return null;
};

export function mapAiAgentAggStreamResponse (response) {
    const { LOADING, NOTIFICATION, REJECTED, RESOLVED } = LOADING_STATES;
    switch (response.status) {
        case LOADING:
        case NOTIFICATION:
            return response;
        case REJECTED:
            if (response.error?.message === 'cancelled') {
                return {
                    status: response.error.message,
                    error: response.error,
                };
            }

            return {
                status: response.status,
                error: response.error,
            };
        case RESOLVED:
            return {
                status: response.status,
                value: response.value,
            };
        default:
            return response;
    }
}

export function createAiAgentReportAggStream ({ aggregation, paramFilter }) {
    const subject = new Subject();
    const param$ = subject.pipe(
        mergeMap(async (aggParams) => {
            return {
                ...aggParams,
            };
        }),
    );

    const connected$ = paramFilter
        ? param$.pipe(
            switchMap((aggParams) => {
                if (!paramFilter(aggParams)) {
                    return of({
                        status: LOADING_STATES.RESOLVED,
                        value: [],
                    });
                }

                return connectAggregationToObservable(
                    aggregation,
                    of(aggParams).pipe(
                        mergeMap(async (param) => {
                            return {
                                ...param,
                            };
                        }),
                    ),
                );
            }),
        )
        : connectAggregationToObservable(aggregation, param$);

    const response$ = connected$.pipe(
        mergeMap(async (response) => mapAiAgentAggStreamResponse(response)),
    );

    return [response$, subject];
}

export function createRunScopedAiAgentReportAggStream ({ aggregation }) {
    const trigger$ = new Subject();
    const response$ = trigger$.pipe(
        switchMap(({ runId, aggParams }) =>
            connectAggregationToObservable(aggregation, of(aggParams)).pipe(
                mergeMap(async (response) => mapAiAgentAggStreamResponse(response)),
                map((response) => ({ ...response, runId })),
            ),
        ),
    );

    return [response$, trigger$];
}

export function isPendoDefaultSegment (segmentId) {
    const defaultIds = Object.keys(getDefaultSegments());

    return defaultIds.includes(segmentId);
}

export function parseAgentDescriptionContext (description = '') {
    if (typeof description !== 'string' || !description.trim()) {
        return { role: '', primaryUsers: '', purpose: '' };
    }

    const roleMatch = description.match(/^Agent role:\s*(.*)$/m);
    const primaryUsersMatch = description.match(/^Primary users:\s*(.*)$/m);
    const purposeMatch = description.match(/^Agent purpose:\s*([\s\S]*)$/m);
    const isStructuredContext = !!(roleMatch || primaryUsersMatch || purposeMatch);

    if (!isStructuredContext) {
        return { role: '', primaryUsers: '', purpose: description.trim() };
    }

    return {
        role: roleMatch?.[1]?.trim() || '',
        primaryUsers: primaryUsersMatch?.[1]?.trim() || '',
        purpose: purposeMatch?.[1]?.trim() || '',
    };
}

// `type` and `conversationsEnabled` are independent: an MCP agent captured in prompts-only mode
// still has conversationsEnabled false, so the type has to win before the capture-mode split.
// Types outside MCP/Autonomous (e.g. visitorAssist) intentionally fall through to that split.
export function getAiAgentTypeBucket (aiAgent, includeAgentTypes = false) {
    if (includeAgentTypes) {
        if (aiAgent?.type === AI_AGENT_TYPES.MCP) return AI_AGENT_TYPES.MCP;
        if (aiAgent?.type === AI_AGENT_TYPES.AUTONOMOUS) return AI_AGENT_TYPES.AUTONOMOUS;
    }

    return aiAgent?.conversationsEnabled ? 'conversational' : 'prompt-only';
}

export function getAiAgentTypeLabel (aiAgent, showAgentTypes = false) {
    const labels = {
        [AI_AGENT_TYPES.MCP]: $t('aiAgent.MCP'),
        [AI_AGENT_TYPES.AUTONOMOUS]: $t('aiAgent.Autonomous'),
        conversational: $t('aiAgent.Conversational'),
        'prompt-only': $t('aiAgent.Prompt only'),
    };

    return labels[getAiAgentTypeBucket(aiAgent, showAgentTypes)];
}

function parseAgentContextField (description, label) {
    if (typeof description !== 'string' || !description.trim()) {
        return '';
    }

    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const startMatch = description.match(new RegExp(`(?:^|\\n)${escapedLabel}:\\s*`));
    if (!startMatch) {
        return '';
    }

    const startIndex = startMatch.index + startMatch[0].length;
    const remainingDescription = description.slice(startIndex);
    const nextSectionMatch = remainingDescription.match(/\n(?:Agent role|Primary users|Agent purpose):\s*/);
    const endIndex = nextSectionMatch ? nextSectionMatch.index : remainingDescription.length;

    return remainingDescription.slice(0, endIndex).trim();
}

export function trackAiAgentSetup ({ user, accountId, setupType, agent }) {
    const agentRole = agent.role || parseAgentContextField(agent.description, 'Agent role');
    const primaryUsers = agent.primaryUsers || parseAgentContextField(agent.description, 'Primary users');
    const agentPurpose = agent.purpose || parseAgentContextField(agent.description, 'Agent purpose');
    const application = agent.application || agent.appName || agent.appId || '';

    const trackObj = {
        event_name: 'Agent Analytics - AI agent set up',
        timestamp: new Date().toISOString(),
        visitor_id: user.username,
        account_id: accountId,
        setup_type: setupType,
        agent_id: agent.id,
        agent_name: agent.name,
        agent_mode: agent.mode,
        agent_role: agentRole,
        primary_users: primaryUsers,
        agent_purpose: agentPurpose,
        application,
    };

    window.pendo.track('Agent Analytics - AI agent set up', trackObj);
}

export function trackAiAgentReportUsage (
    isSavedReport,
    {
        reportId,
        subId,
        appId,
        aiAgentId,
        aiAgentName,
        timeSeries,
        segmentId,
        allAccounts,
        numberOfTimesReportHasRunBeforeSaveOrUpdate,
        trackedUseCases,
        filteredByCreatedBy,
        filteredByFavorites,
    },
) {
    const tucFilterValues = [];
    let numberOfTucFilters = 0;
    const tucFilterApplied = trackedUseCases && trackedUseCases.length > 0;

    if (tucFilterApplied) {
        tucFilterValues.push(...trackedUseCases.map((tuc) => tuc.name));
        numberOfTucFilters = trackedUseCases.length;
    }

    const trackObj = {
        reportId,
        subId,
        appId,
        aiAgentId,
        aiAgentName,
        timeSeries,
        isPendoDefaultSegment: isPendoDefaultSegment(segmentId),
        allAccounts,
        numberOfTimesReportHasRunBeforeSaveOrUpdate,
        filterbyTUC: tucFilterApplied,
        numberTUCFilter: numberOfTucFilters,
        valuesTUCFilter: tucFilterValues,
        filterbyCreated: filteredByCreatedBy,
        filterbyFavorites: filteredByFavorites,
    };
    window.pendo.track(isSavedReport ? 'Update AI Agent Report' : 'Save AI Agent Report', trackObj);
}

export function trackTUCUsage ({ eventType, newTuc, oldTuc, user, sub, timestamp, createdFromEuc }) {
    const newTucCreated = eventType === 'create';
    const account = sub?.displayName || '';

    const trackObj = {
        event_type: eventType,
        use_case_name: newTuc.name,
        use_case_name_OLD: newTucCreated ? null : (oldTuc?.name || oldTuc?.clusterName),
        description: newTuc.description,
        description_OLD: newTucCreated ? null : (oldTuc?.description || oldTuc?.clusterSummary),
        visitor: user.username,
        user_job_role: user.jobRole,
        account,
        timestamp,
        created_from_EUC: createdFromEuc,
    };

    window.pendo.track('Save Tracked Use Case', trackObj);
}

export function trackTrackedIssueUsage ({ eventType, newIssue, oldIssue, user, sub, timestamp, createdFromEmergentIssue }) {
    const newIssueCreated = eventType === 'create';
    const account = sub?.displayName || '';

    const trackObj = {
        event_type: eventType,
        issue_name: newIssue.name || newIssue.issueName,
        issue_name_OLD: newIssueCreated ? null : (oldIssue?.name || oldIssue?.issueName),
        description: newIssue.description || newIssue.summary,
        description_OLD: newIssueCreated ? null : (oldIssue?.description || oldIssue?.summary),
        visitor: user.username,
        user_job_role: user.jobRole,
        account,
        timestamp,
        created_from_emergent_issue: createdFromEmergentIssue,
    };

    window.pendo.track('Save Tracked Issue', trackObj);
}

export const AGENT_ANALYTICS_SAVE_CUSTOM_METRIC_TRACK_EVENT = 'Agent Analytics - Save Custom Metric';

function resolveCustomMetricSelectedEventFields (selectedEvent) {
    if (!selectedEvent?.name && !selectedEvent?.kind) {
        return {
            has_event: false,
            selected_event_name: '',
            selected_event_kind: '',
        };
    }

    return {
        has_event: true,
        selected_event_name: selectedEvent.name || '',
        selected_event_kind: selectedEvent.kind || '',
    };
}

export function trackCustomMetricUsage ({
    eventType,
    newMetric,
    oldMetric,
    user,
    sub,
    timestamp,
    agent,
    subId,
    newSelectedEvent,
    oldSelectedEvent,
}) {
    if (typeof window === 'undefined' || !window.pendo?.track) {
        return;
    }

    const isCreate = eventType === 'create';
    const account = sub?.displayName || '';
    const agentRole = agent?.role || parseAgentContextField(agent?.description, 'Agent role');
    const agentPurpose = agent?.purpose || parseAgentContextField(agent?.description, 'Agent purpose');
    const newEventFields = resolveCustomMetricSelectedEventFields(newSelectedEvent);
    const oldEventFields = resolveCustomMetricSelectedEventFields(oldSelectedEvent);

    const trackObj = {
        event_type: eventType,
        metric_name: newMetric?.name || '',
        metric_name_OLD: isCreate ? null : (oldMetric?.name || ''),
        description: newMetric?.description || '',
        description_OLD: isCreate ? null : (oldMetric?.description || ''),
        ...newEventFields,
        selected_event_name_OLD: isCreate ? null : (oldEventFields.selected_event_name || null),
        selected_event_kind_OLD: isCreate ? null : (oldEventFields.selected_event_kind || null),
        golden_rules_count: Array.isArray(newMetric?.goldenRules) ? newMetric.goldenRules.length : 0,
        ai_agent_id: agent?.id || '',
        ai_agent_name: agent?.name || '',
        agent_role: agentRole,
        agent_purpose: agentPurpose,
        subId: subId || sub?.id || '',
        visitor: user?.username || '',
        user_job_role: user?.jobRole || '',
        account,
        timestamp,
    };

    window.pendo.track(AGENT_ANALYTICS_SAVE_CUSTOM_METRIC_TRACK_EVENT, trackObj);
}

/** Pendo event when Agent Analytics Slack digest settings are saved or the digest is turned off. */
export const AGENT_ANALYTICS_SLACK_DIGEST_TRACK_EVENT = 'Agent Analytics Slack Digest';

/**
 * @param {object} params
 * @param {string} params.frequency - Cadence label, e.g. Daily, Weekly, Monthly
 * @param {string} params.segment - Segment display name
 * @param {boolean} params.turn_off - True when the user disables the digest
 */
export function trackAgentAnalyticsSlackDigest ({ frequency, segment, turn_off: turnOff }) {
    if (typeof window === 'undefined' || !window.pendo?.track) {
        return;
    }

    window.pendo.track(AGENT_ANALYTICS_SLACK_DIGEST_TRACK_EVENT, {
        frequency,
        segment,
        turn_off: turnOff,
    });
}

export const AGENT_ANALYTICS_TEAMS_DIGEST_TRACK_EVENT = 'Agent Analytics Teams Digest';

export function trackAgentAnalyticsTeamsDigest ({ frequency, segment, turn_off: turnOff }) {
    if (typeof window === 'undefined' || !window.pendo?.track) {
        return;
    }

    window.pendo.track(AGENT_ANALYTICS_TEAMS_DIGEST_TRACK_EVENT, {
        frequency,
        segment,
        turn_off: turnOff,
    });
}

export async function fetchAiAgents () {
    const { data } = await http.get(`${aiAgentsApi}/aiagent`);

    return data;
}

export async function fetchExperiments ({ isSuper = false } = {}) {
    const url = isSuper
        ? '/api/super/agentanalytics/experiments'
        : `${aiAgentsApi}/experiments`;
    const { data } = await http.get(url);

    return data;
}

export async function fetchAiAgent (id) {
    const { data } = await http.get(`${aiAgentsApi}/aiagent/${id}`);

    return data;
}

export async function updateAiAgent (id, patchUpdates) {
    const { data } = await http.patch(`${aiAgentsApi}/aiagent/${id}`, {
        patch: patchUpdates,
    });

    return data;
}

export async function deleteAiAgent (id) {
    return http.delete(`${aiAgentsApi}/aiagent/${id}`);
}

export async function testRedaction (input, redactionClasses) {
    const { data } = await http.post(`${aiAgentsApi}/redaction/test`, { input, redactionClasses });

    return data;
}

export function createAgent (agentData) {
    return http.post(`${aiAgentsApi}/aiagent`, {
        name: agentData.name,
        description: agentData.description,
        appId: agentData.appId,
        conversationsEnabled:
            agentData.conversationsEnabled || agentData.taggingMode === 'fullConversations',
        zeroRetentionEnabled: !!agentData.zeroRetentionEnabled,
        ...retentionWindowField(agentData.dataRetention),
        deIdentifyVisitors: !!agentData.deIdentifyVisitors,
        cssSelectors: [],
        privacyFilters: '',
        pageId: null,
        ...(agentData.preset ? { preset: agentData.preset } : {}),
        ...(agentData.type ? { type: agentData.type } : {}),
        ...(agentData.orbColorClass ? {
            orbColorClass: agentData.orbColorClass,
            orbVariationSeed: agentData.orbVariationSeed ?? 0,
        } : {}),
    });
}

export const AI_AGENT_PROVIDER_BASE_DOMAINS = {
    'chatgpt-full': ['chatgpt.com', 'chat.openai.com'],
    'gemini-full': ['gemini.google.com'],
    'claude-full': ['claude.ai'],
    'copilot-full': ['copilot.microsoft.com'],
    'copilot-m365-full': ['m365.cloud.microsoft', 'copilot.cloud.microsoft'],
};

export function getThirdPartyAIAgentProviderFromApp (app) {
    const extensionDomains = app?.extensionDomainPatterns;
    if (!Array.isArray(extensionDomains) || extensionDomains.length === 0) {
        return null;
    }

    return Object.keys(AI_AGENT_PROVIDER_BASE_DOMAINS).find((provider) => {
        return AI_AGENT_PROVIDER_BASE_DOMAINS[provider].some((baseDomain) =>
            extensionDomains.includes(baseDomain),
        );
    }) ?? null;
}

export function shouldEnableSampling (numPrompts) {
    return numPrompts >= SAMPLING_THRESHOLD;
}

export function isTrackedUseCaseProcessing (trackedUseCase) {
    return trackedUseCase?.status === TRACKED_USE_CASE_STATUS.PROCESSING;
}

export function isTrackedIssueProcessing (trackedIssue) {
    return trackedIssue?.status === TRACKED_USE_CASE_STATUS.PROCESSING;
}

export function isTrackedIntentMetricProcessing (trackedIntentMetric) {
    return trackedIntentMetric?.status === TRACKED_USE_CASE_STATUS.PROCESSING;
}

function normalizeCreatedByUser (createdByUser) {
    if (!createdByUser) return null;
    if (typeof createdByUser === 'string') {
        return { username: createdByUser, id: createdByUser };
    }

    return createdByUser;
}

function getCreatedByIdentifiers (createdByUser) {
    const user = normalizeCreatedByUser(createdByUser);
    if (!user) return [];

    return [user.id, user.userid, user.username]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
}

export function isTrackedIntentMetricCreatedByUser (metric, currentUser) {
    if (!metric || !currentUser) return false;

    const creatorIds = getCreatedByIdentifiers(metric.createdByUser);
    const currentUserIds = [currentUser.id, currentUser.userid, currentUser.username]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

    return creatorIds.some((id) => currentUserIds.includes(id));
}

export function getTrackedIntentMetricCreatorUsername (metric) {
    const user = normalizeCreatedByUser(metric?.createdByUser);
    if (!user) return '';

    return user.username || user.userid || user.id || '';
}

export function aiAgentsTruncatedLabel (trackedUseCases, firstSelected = '') {
    if (trackedUseCases && trackedUseCases.length) {
        const firstVal = firstSelected || trackedUseCases[0].name;

        if (trackedUseCases.length > 1) {
            return `${firstVal} + ${trackedUseCases.length - 1} ${$t(
                'aiAgent.more',
            )}`;
        }

        return firstVal;
    }

    return '';
};

export function sortAndFormatCreatedByOptions (users) {
    return [...new Set(
        users
            .filter((user) => typeof user === 'string')
            .map((user) => user.toLowerCase())
            .sort((a, b) => a.localeCompare(b)),
    )];
};

export function getRagePromptTypeLabel (ragePromptType) {
    switch (ragePromptType) {
        case 'allCaps':
            return 'Rage prompt: Uppercase text';
        case 'profanity':
            return 'Rage prompt: Profanity';
        case 'rephrasedQuestionManyTimes':
            return 'Rage prompt: Repeated attempt';
        default:
            return 'Rage prompt';
    }
}

export function filterTrackedUseCaseIdsByStatus ({ ids = [], map = {}, includeProcessing = false }) {
    return ids.filter((id) => {
        const trackedUseCase = map[id];
        const statuses = [TRACKED_USE_CASE_STATUS.STABLE];
        if (includeProcessing) {
            statuses.push(TRACKED_USE_CASE_STATUS.PROCESSING);
        }

        return statuses.includes(trackedUseCase?.status);
    });
}

export function hasActiveEmergentClusterFilters ({
    selectedAccount,
    selectedExperiment,
    allowTrackedUseCaseFilterOnEmergent,
    stableSelectedTrackedUseCaseIds = [],
    hasMetadataFiltersEnabled,
    userReactionFilter,
    fileUploadedFilter,
    usedSuggestedPromptFilter,
    toolsUsedFilter,
    modelsUsedFilter,
    subagentsUsedFilter,
    inputTokenCountFilter,
    outputTokenCountFilter,
    customPropertyFilters,
}) {
    if (selectedAccount?.id) {
        return true;
    }

    if (selectedExperiment) {
        return true;
    }

    if (allowTrackedUseCaseFilterOnEmergent && stableSelectedTrackedUseCaseIds.length > 0) {
        return true;
    }

    if (hasMetadataFiltersEnabled) {
        return hasAnyMetadataFilter({
            userReactionFilter,
            fileUploadedFilter,
            usedSuggestedPromptFilter,
            toolsUsedFilter,
            modelsUsedFilter,
            subagentsUsedFilter,
            inputTokenCountFilter,
            outputTokenCountFilter,
            customPropertyFilters,
        });
    }

    return false;
}

export function buildEmergentClusterFilterStateFromAggConfig (
    aggConfig,
    { allowTrackedUseCaseFilterOnEmergent },
) {
    const hasMetadataFiltersEnabled = aggConfig.userReactionFilter !== undefined;

    return {
        selectedAccount: aggConfig.selectedAccount,
        selectedExperiment: aggConfig.experimentVersion ?? null,
        allowTrackedUseCaseFilterOnEmergent,
        stableSelectedTrackedUseCaseIds: aggConfig.selectedTrackedUseCaseIds ?? [],
        hasMetadataFiltersEnabled,
        userReactionFilter: aggConfig.userReactionFilter,
        fileUploadedFilter: aggConfig.fileUploadedFilter,
        usedSuggestedPromptFilter: aggConfig.usedSuggestedPromptFilter,
        toolsUsedFilter: aggConfig.toolsUsedFilter,
        modelsUsedFilter: aggConfig.modelsUsedFilter,
        subagentsUsedFilter: aggConfig.subagentsUsedFilter,
        inputTokenCountFilter: aggConfig.inputTokenCountFilter,
        outputTokenCountFilter: aggConfig.outputTokenCountFilter,
        customPropertyFilters: aggConfig.customPropertyFilters,
    };
}

export function buildBaselineEmergentClusteringAggConfig (aggConfig) {
    return {
        ...aggConfig,
        selectedAccount: { id: '', text: 'All accounts' },
        selectedTrackedUseCaseIds: [],
        experimentVersion: null,
        userReactionFilter: undefined,
        fileUploadedFilter: undefined,
        usedSuggestedPromptFilter: undefined,
        toolsUsedFilter: undefined,
        modelsUsedFilter: undefined,
        subagentsUsedFilter: undefined,
        inputTokenCountFilter: undefined,
        outputTokenCountFilter: undefined,
        customPropertyFilters: undefined,
    };
}

export function buildFilteredEmergentClusteringAggConfig (aggConfig, allowTrackedUseCaseFilterOnEmergent) {
    return {
        ...aggConfig,
        selectedTrackedUseCaseIds: allowTrackedUseCaseFilterOnEmergent
            ? aggConfig.selectedTrackedUseCaseIds
            : [],
    };
}

export function isReplayExpired (createdAtTimestamp, replayRetentionDays) {
    if (!createdAtTimestamp || !replayRetentionDays) {
        return false;
    }

    const retentionMilliseconds = replayRetentionDays * 24 * 60 * 60 * 1000;
    const now = Date.now();

    return now - createdAtTimestamp > retentionMilliseconds;
}

/**
 * Extracts event IDs from issue eventReferences.
 * eventReferences can be objects with eventId property or plain strings.
 * @param {Array} eventReferences - Array of event references (objects or strings)
 * @returns {Array<string>} - Array of event ID strings
 */
export function extractEventIdsFromReferences (eventReferences = []) {
    return eventReferences
        .map((ref) => (typeof ref === 'object' && ref !== null ? ref.eventId : ref))
        .filter(Boolean);
}

export function resolveVisitorDisplayName ({ visitorId, visitor, configuredPath, visitorDisplayNameSetting }) {
    if (!visitorId) return '';
    if (!visitor) return visitorId;

    const mapped = getVisitorDisplayName({ visitor, visitorId, setting: visitorDisplayNameSetting });
    if (mapped != null) return mapped;

    const email = (configuredPath && get(visitor, `metadata.${configuredPath}`, '')) ||
        get(visitor, 'metadata.agent.email', '');

    return email || visitorId;
}

export function resolveAccountDisplayName ({ accountId, account, configuredPath }) {
    if (!accountId) return '';
    if (!account) return accountId;

    const displayName = (configuredPath && get(account, `metadata.${configuredPath}`, '')) ||
        get(account, 'metadata.agent.display_name', '') ||
        get(account, 'metadata.agent.name', '');

    return displayName || accountId;
}

export async function createSlackDigestSchedule (scheduleData, agentId) {
    const { data } = await http.post(`/api/s/_SID_/agentanalytics/aiagent/${agentId}/slackdigest`, scheduleData);

    return data;
}

export async function updateSlackDigestSchedule (digestId, scheduleData, agentId) {
    const { data } = await http.put(`/api/s/_SID_/agentanalytics/aiagent/${agentId}/slackdigest/${digestId}`, scheduleData);

    return data;
}

export async function deleteSlackDigestSchedule (digestId, agentId) {
    return http.delete(`/api/s/_SID_/agentanalytics/aiagent/${agentId}/slackdigest/${digestId}`);
}

export async function fetchSlackDigestSchedules (agentId) {
    const url = agentId
        ? `/api/s/_SID_/agentanalytics/aiagent/${agentId}/slackdigest`
        : `${slackDigestApi}`;
    const { data } = await http.get(url);

    return data;
}

export async function fetchSlackDigestSchedule (digestId) {
    const { data } = await http.get(`${slackDigestApi}/${digestId}`);

    return data;
}

// For use in AA tables being opened from shared links
// Aggs can be slow to load, so we need to wait for them to be ready before navigating to a row
// or performing any other actions on the table
export function waitForTableAndDataLoad (tableRef, data, maxRetries = 5) {
    return new Promise((resolve) => {
        let retryCount = 0;
        const check = () => {
            if (tableRef && data.length) {
                resolve(true);
            } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(check, 100 * retryCount);
            } else {
                resolve(false);
            }
        };
        check();
    });
}

// Share links must carry the /s/<subId> segment so they resolve in this
// subscription for recipients whose active subscription differs.
export function getAgentAnalyticsReportBaseUrl () {
    const subId = getActiveSubscription()?.id;
    const subPath = subId ? `/s/${subId}` : '';

    return `${window.location.origin}${subPath}/agent-analytics/report`;
}

export function buildConversationShareUrl ({
    aiAgentId,
    conversationId,
    messageId,
    reportDateRange,
    savedReportId,
    sourceContext = {},
}) {
    if (!conversationId || !messageId) return '';

    const rawVersionQueryParam = getFrontendVersion(window.location.href);
    const versionParam = rawVersionQueryParam ? `version=${rawVersionQueryParam}` : '';

    const agentParam = `aiAgentId=${aiAgentId}`;
    const conversationParam = `conversationId=${conversationId}`;
    const messageParam = `messageId=${messageId}`;

    const contextToParam = {
        tableType: 'sourceTableType',
        type: 'sourceType',
        clusterId: 'sourceClusterId',
        clusterName: 'sourceClusterName',
        rowId: 'sourceRowId',
    };
    const sourceParams = Object.entries(contextToParam)
        .filter(([key]) => sourceContext[key])
        .map(([key, paramName]) => {
            const value = sourceContext[key];
            const encodedValue = key === 'clusterName' ? encodeURIComponent(value) : value;

            return `${paramName}=${encodedValue}`;
        })
        .join('&');

    if (savedReportId) {
        const params = [conversationParam, messageParam, sourceParams, versionParam].filter(Boolean).join('&');

        return `${getAgentAnalyticsReportBaseUrl()}/${savedReportId}?${params}`;
    }

    const dateParams = reportDateRange?.config ? formatDateRangeForQuery({ timeSeries: reportDateRange }) : {};
    if (dateParams.start && dateParams.end) {
        const params = [agentParam, conversationParam, messageParam, `start=${dateParams.start}`, `end=${dateParams.end}`, sourceParams, versionParam].filter(Boolean).join('&');

        return `${getAgentAnalyticsReportBaseUrl()}?${params}`;
    }

    const dateType = dateParams.dateType || '30';
    const params = [agentParam, conversationParam, messageParam, `dateType=${dateType}`, sourceParams, versionParam].filter(Boolean).join('&');

    return `${getAgentAnalyticsReportBaseUrl()}?${params}`;
}

export function buildPromptShareUrl ({
    aiAgentId,
    promptEventId,
    reportDateRange,
    savedReportId,
    sourceContext = {},
    visitorId,
    browserTime,
}) {
    if (!promptEventId && !(visitorId && browserTime)) return '';

    const rawVersionQueryParam = getFrontendVersion(window.location.href);
    const versionParam = rawVersionQueryParam ? `version=${rawVersionQueryParam}` : '';

    const agentParam = `aiAgentId=${aiAgentId}`;
    const promptParam = promptEventId ? `promptEventId=${promptEventId}` : '';
    const visitorParam = visitorId ? `promptVisitorId=${encodeURIComponent(visitorId)}` : '';
    const timeParam = browserTime ? `promptBrowserTime=${browserTime}` : '';

    const contextToParam = {
        tableType: 'sourceTableType',
        type: 'sourceType',
        clusterId: 'sourceClusterId',
        clusterName: 'sourceClusterName',
        rowId: 'sourceRowId',
    };
    const sourceParams = Object.entries(contextToParam)
        .filter(([key]) => sourceContext[key])
        .map(([key, paramName]) => {
            const value = sourceContext[key];
            const encodedValue = key === 'clusterName' ? encodeURIComponent(value) : value;

            return `${paramName}=${encodedValue}`;
        })
        .join('&');

    if (savedReportId) {
        const params = [promptParam, visitorParam, timeParam, sourceParams, versionParam].filter(Boolean).join('&');

        return `${getAgentAnalyticsReportBaseUrl()}/${savedReportId}?${params}`;
    }

    const dateParams = reportDateRange?.config ? formatDateRangeForQuery({ timeSeries: reportDateRange }) : {};
    if (dateParams.start && dateParams.end) {
        const params = [agentParam, promptParam, visitorParam, timeParam, `start=${dateParams.start}`, `end=${dateParams.end}`, sourceParams, versionParam].filter(Boolean).join('&');

        return `${getAgentAnalyticsReportBaseUrl()}?${params}`;
    }

    const dateType = dateParams.dateType || '30';
    const params = [agentParam, promptParam, visitorParam, timeParam, `dateType=${dateType}`, sourceParams, versionParam].filter(Boolean).join('&');

    return `${getAgentAnalyticsReportBaseUrl()}?${params}`;
}

export function buildReplayShareUrl (replayContext, router) {
    if (!replayContext?.recordingSessionId || !router) return '';

    const route = buildReplayRecordingRouteObject({ replayContext });
    if (!route) return '';

    const resolved = router.resolve(route);

    return resolved?.href ? `${window.location.origin}${resolved.href}` : '';
}

export function getReplayLinkContent ({
    hasSessionReplayEnabled,
    replay,
    browserTime,
    replayRetentionDays,
    router,
    replayLinkNotAvailableMessage,
}) {
    if (!hasSessionReplayEnabled) {
        return replayLinkNotAvailableMessage || $t('aiAgent.createTicketModal.replayLinkNotAvailable');
    }

    if (!replay?.recordingSessionId || isReplayExpired(browserTime, replayRetentionDays)) {
        return null;
    }

    return buildReplayShareUrl(replay, router) || null;
}

export const agentComparisonApi = `${aiAgentsApi}/comparison`;

export async function fetchAgentComparisons () {
    const { data } = await http.get(agentComparisonApi);

    return data;
}

export async function createAgentComparison (comparison) {
    const { data } = await http.post(agentComparisonApi, comparison);

    return data;
}

export async function getAgentComparison (id) {
    const { data } = await http.get(`${agentComparisonApi}/${id}`);

    return data;
}

export async function updateAgentComparison (id, patch) {
    const { data } = await http.patch(`${agentComparisonApi}/${id}`, patch);

    return data;
}

export async function deleteAgentComparison (id) {
    return http.delete(`${agentComparisonApi}/${id}`);
}

export async function generateComparisonSummary (id, metricData) {
    const { data } = await http.post(`${agentComparisonApi}/${id}/summary`, { metricData });

    return data;
}

export const COMPARISON_STATUS = {
    IN_PROGRESS: 'inProgress',
    COMPLETED: 'completed',
};

/**
 * Derives status purely from the stored date ranges: completed once every group's range has ended.
 * End dates are stored exclusive (last day + 1), so endMs <= now means the range is over.
 * Returns null when no range is parseable, i.e. the caller should leave status alone.
 */
export function deriveComparisonStatusFromConditionRows (conditionRows = [], now = Date.now()) {
    const dateRangeRow = (conditionRows || []).find((row) => row.parameterId === 'dateRange');
    const endDates = (dateRangeRow?.groupValues || []).reduce((acc, rangeStr) => {
        const [, endStr] = (rangeStr || '').split(' → ');
        const parsed = Date.parse((endStr || '').trim());
        if (!isNaN(parsed)) {
            acc.push(parsed);
        }

        return acc;
    }, []);

    if (!endDates.length) {
        return null;
    }

    return endDates.every((endMs) => endMs <= now) ? COMPARISON_STATUS.COMPLETED : COMPARISON_STATUS.IN_PROGRESS;
}

export function getMetricsGridAggStatus ({
    aggStatus,
    customMetricsAggStatus = LOADING_STATES.LOADING,
    trackedIntentMetrics = [],
}) {
    if (aggStatus === LOADING_STATES.LOADING) {
        return LOADING_STATES.LOADING;
    }

    if (trackedIntentMetrics.length > 0 && customMetricsAggStatus === LOADING_STATES.LOADING) {
        return LOADING_STATES.LOADING;
    }

    if (aggStatus === LOADING_STATES.REJECTED) {
        return LOADING_STATES.REJECTED;
    }

    return LOADING_STATES.RESOLVED;
}

export function emergentUseCasesTableShowsEmptyState ({
    isLoadingResources,
    clusteringAggStatus,
    keyMetricsAggStatus,
    showMinimumPromptsEmptyState,
    showClusteringErrorState,
    clusteringAggData = [],
}) {
    if (
        isLoadingResources
        || clusteringAggStatus === LOADING_STATES.LOADING
        || keyMetricsAggStatus === LOADING_STATES.LOADING
    ) {
        return false;
    }

    return showMinimumPromptsEmptyState
        || showClusteringErrorState
        || clusteringAggData.length === 0;
}

export function emergentIssuesTableShowsEmptyState ({
    isLoadingResources,
    detectIssuesAggStatus,
    detectIssuesAggData = [],
}) {
    if (isLoadingResources || detectIssuesAggStatus === LOADING_STATES.LOADING) {
        return false;
    }

    return detectIssuesAggData.length === 0;
}

export function trackedUseCasesTableShowsEmptyState ({
    isLoadingResources,
    trackedUseCasesAggStatus,
    filteredTrackedUseCases = [],
}) {
    if (
        isLoadingResources
        || trackedUseCasesAggStatus === LOADING_STATES.LOADING
    ) {
        return false;
    }

    return filteredTrackedUseCases.length === 0;
}

export function trackedIssuesTableShowsEmptyState ({
    isLoadingResources,
    trackedIssuesAggStatus,
    filteredTrackedIssues = [],
}) {
    if (
        isLoadingResources
        || trackedIssuesAggStatus === LOADING_STATES.LOADING
    ) {
        return false;
    }

    return filteredTrackedIssues.length === 0;
}

export const VISITOR_ASSIST_INTEGRATION_PROVIDER = 'visitorAssist';

const NORMALIZED_AGENT_ANALYTICS_FILTER_QUERY_KEYS = ['aiAgentId', 'dateType', 'start', 'end'];

function normalizeAgentAnalyticsFilterQueryValue (value) {
    if (Array.isArray(value)) {
        return value[0];
    }

    return value;
}

function getNormalizedAgentAnalyticsFilterQuery (query = {}) {
    return {
        aiAgentId: normalizeAgentAnalyticsFilterQueryValue(query.aiAgentId),
        dateType: normalizeAgentAnalyticsFilterQueryValue(query.dateType),
        start: normalizeAgentAnalyticsFilterQueryValue(query.start ?? query.startDate),
        end: normalizeAgentAnalyticsFilterQueryValue(query.end ?? query.endDate),
    };
}

/**
 * Compares agent analytics filter-related route query params, treating start/startDate
 * and end/endDate aliases as equivalent.
 * @param {Record<string, unknown>} [newQuery={}]
 * @param {Record<string, unknown>} [oldQuery={}]
 * @returns {boolean}
 */
export function haveAgentAnalyticsFilterQueryChanged (newQuery = {}, oldQuery = {}) {
    const normalizedNewQuery = getNormalizedAgentAnalyticsFilterQuery(newQuery);
    const normalizedOldQuery = getNormalizedAgentAnalyticsFilterQuery(oldQuery);

    return NORMALIZED_AGENT_ANALYTICS_FILTER_QUERY_KEYS.some(
        (key) => normalizedNewQuery[key] !== normalizedOldQuery[key],
    );
}

/**
 * Whether a user may edit/delete/resave an Agent Analytics saved report.
 * Matches experiment-report mutate rules: owner, any sub admin / AI Agent Admin,
 * or holder of the shared-report mutate permission.
 *
 * @param {Object} params
 * @param {Object} [params.report]
 * @param {string} [params.userId]
 * @param {boolean} [params.isAdminOrAiAgentAdmin] - true if the user is a sub admin or AI Agent Admin for any app
 * @param {boolean} [params.hasSharedMutatePermission]
 * @param {boolean} [params.treatMissingCreatorAsOwned] - true for unsaved reports on the report page
 * @returns {boolean}
 */
export function canMutateAiAgentReport ({
    report,
    userId,
    isAdminOrAiAgentAdmin = false,
    hasSharedMutatePermission = false,
    treatMissingCreatorAsOwned = false,
}) {
    const createdByUserId = get(report, 'createdByUser.id');
    const isSavedReport = Boolean(get(report, 'id'));

    const isOwnReport = treatMissingCreatorAsOwned
        ? (!createdByUserId || createdByUserId === userId)
        : (isSavedReport && createdByUserId === userId);

    if (isOwnReport) {
        return true;
    }

    if (isAdminOrAiAgentAdmin) {
        return true;
    }

    return Boolean(hasSharedMutatePermission);
}

export const AGG_CONFIG_EXCLUDED_KEYS = ['aiAgentName', 'clusterGranularity', 'issueGranularity'];

// aggConfig is rebuilt (new reference) on every ai-agent-report.vue filter/tab change, so a plain
// deep watch on it re-subscribes every sibling widget even when only a field that widget doesn't
// read (e.g. clusterGranularity) changed. Widgets watch this fingerprint instead: excluding fields
// they've confirmed their own aggregation pipeline never reads keeps the string identical (Vue's
// computed skips re-triggering dependents when the returned value is unchanged) across changes
// that are genuinely irrelevant to them, while any field they do read still trips it normally.
// Only top-level keys are sorted; a nested value (e.g. a filter object) rebuilt with different key
// insertion order but identical content will spuriously change the fingerprint (extra re-fetch, never a missed one).
export function fingerprintAggConfig (aggConfig, excludeKeys = []) {
    if (!aggConfig) return '';

    const relevantEntries = Object.keys(aggConfig)
        .filter((key) => !excludeKeys.includes(key))
        .sort()
        .map((key) => [key, aggConfig[key]]);

    return JSON.stringify(relevantEntries);
}
